// Third-party
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useState, useRef, useEffect } from 'react';
import { faExpand, faFrownOpen, faLaughBeam, faDesktop, faMeh, faStar, faStarHalf } from '@fortawesome/free-solid-svg-icons';

// First-party
import useNotifications from '@lib/hooks/notifications';
import { gameToLdJson } from '../../common/util/gameToLdJson';
import getMetaDescription from '@lib/util/getMetaDescription';
import Meta from '@lib/components/head/Meta';
import { Slot, If, For, createPage, createComponent, toClassName } from '@lib/util/templateHelpers';

// Component lib
import storage from '@lib/util/storage';
import shared from '@lib/components';
import { fadeOut } from '@lib/transitions/fade';
import rating from '@lib/util/rating';
// import MiniHeaderGamesSendMessages from '@lib/components/MiniHeaderGamesSendMessages';

// Local
import styles from './index.module.scss';
import gameApi from '@/apis/drupal/game';
import routerApi from '@/apis/drupal/router';
import { useAuthStore } from '@/stores/auth';
import FillScreenLayout from '@/layouts/FillScreenLayout';
import GameLayout from '@/layouts/GameLayout';

// Local components
import GameTag from '@/components/GameTag';
import IoGameTile from '@/components/IoGameTile';
import CallToActionTile from '@/components/CallToActionTile';

function convertLegacyRating(voteAverage, totalVotes, minVotes = 5) {
	if (voteAverage === null || totalVotes < minVotes) return null;
	return ((voteAverage + 1) / 2) * 4 + 1;
  }

const {
  Block,
  Flex,
  Button,
  Title,
  GamePlayer,
  Icon,
  Loader,
  Content,
  Box
} = shared;

const demoFadeOut = fadeOut();

const ratingFormatter = (rating) => ((rating / 100) * 4 + 1);

const GameRating = createComponent('PlayPage__GameRating', {}, function GameRating (_, props) {
  const { notify } = useNotifications();
  const [ state ] = useAuthStore();

  const GOOD = 1;
  const MEH = 0;
  const BAD = -1;
  let UNRATED = null;

	UNRATED = 0;
	const MAX_RATING = 5;

  const [ currentRating, setCurrentRating ] = useState(UNRATED);
  const [ sendingRating, setSendingRating ] = useState(false);
  const [ hasRated, setHasRated ] = useState(UNRATED);

  useEffect(() => {
    const { displayRating } = props.game;

	setCurrentRating(displayRating);


    // if (!ratingAverage ||  rating < 1) {
    //   const scaled = convertLegacyRating(rating, totalVotes);
    //   setCurrentRating(scaled ?? 0);
    // } else {
    //   setCurrentRating(ratingAverage ?? 0);
    // }
  }, [props.game.rating, props.game.ratingAverage, props.game.totalVotes]);


  const ratingClassName = toClassName('PlayPage__GamePlayer__Controls__Rating', {
	good: props.game.rating >= 4,
	meh: props.game.rating > 3 && props.game.rating < 3.9,
	bad: props.game.rating <= 2.9
  });

  const ratingButtonClassName0 = toClassName('PlayPage__GamePlayer__Controls__Icon', {
    rated: hasRated !== UNRATED,
    picked: hasRated === GOOD,
    meh: hasRated === MEH,
    notPicked: hasRated === BAD,
    loading: !!sendingRating
  });

  const ratingButtonClassName1 = toClassName('PlayPage__GamePlayer__Controls__Icon', {
    picked: hasRated === BAD,
    meh: hasRated === MEH,
    notPicked: hasRated === GOOD,
    loading: !!sendingRating
  });

  const rateGame = async (userRated) => {
    setSendingRating(true);
    setHasRated(userRated);

    const result = await gameApi.rateGame(props.entityId, state.user?.attributes.drupal_internal__uid || 0, userRated);
    if (result.error) {
      setSendingRating(false);
      setHasRated(UNRATED);
      notify(`There was an error rating this game. ${result.error}`);
      return;
    }

    if (result?.rating) {
      setCurrentRating(result.rating);
    }

    setSendingRating(false);
    setHasRated(userRated);
    notify('Thank you for voting!');
    storage.setLocal(`game-rated-${state.user?.id || 0}-${props.entityId}`, userRated);
  };

  useEffect(() => {
    if (!state.user?.id) {
      const userRating = storage.getLocal(`game-rated-${state.user?.id || 0}-${props.entityId}`);
      if (userRating !== null) {
        setHasRated(Number(userRating));
      }
    } else {
      gameApi.getRatingForUser(props.entityId, state.user?.attributes.drupal_internal__uid || 0)
        .then((result) => {
          setHasRated(Number(result?.vote_value || 0));
        });
    }

  }, [ state, props.entityId ]);

  return (
    <>
      	<Flex className={ratingClassName} alignCenter>
			<span>Rating</span>
			<span>{currentRating?.toFixed(1) || 'Unrated'}</span>
		</Flex>
		<Flex>
			<Flex gap0 container>
				{Array.from({ length: MAX_RATING }, (_, i) => (
				<Flex
					key={i}
					className={toClassName('PlayPage__GamePlayer__Controls__Icon', {
					picked: hasRated > i,
					loading: !!sendingRating
					})}
					onClick={() => rateGame(i + 1)}
				>
					<Icon.FA icon={faStar} />
				</Flex>
				))}
			</Flex>
		</Flex>
    </>
  );
});

async function fetchProps({ params, preview }) {
	if (preview) {
	  return {
		props: {
		  preview: true,
		  previewPath: params.game,
		}
	  };
	}
  
	const route = await routerApi.translatePath(`/${params.game}`);
	if (route.error) {
	  console.error(`[translatePath error] game: ${params.game}`, route.error);
	  return { notFound: true };
	}
  
	const game = await gameApi.getById(route.entity.uuid, !preview);
	if (game.error) {
	  console.error(`[getById error] game: ${params.game}`, game.error);
	  return { notFound: true };
	}
  
	const gamesByCategory = await Promise.all(
	  game.categories.map(async (category) => {
		const result = await gameApi.listByCategory(category.id, 10);
		if (result.error) {
		  console.error(`[listByCategory error] game: ${params.game}, categoryId: ${category.id}`, result.error);
		  return [];
		}
		return result;
	  })
	);
  
	const relatedGames = gamesByCategory
	  .reduce((acc, games) => acc.concat(games), [])
	  .filter((game, i, arr) => arr.findIndex((found) => found.id === game.id) === i);
  
	const randomIndex = Math.round(Math.random() * (relatedGames.length - 1));
  
	return {
	  props: {
		game: JSON.parse(JSON.stringify(game)), // sanitize
		entityId: Number(route.entity.id),
		randomIndex,
		relatedGames
	  }
	};
  }

export async function getServerSideProps (ctx) {
  return await fetchProps(ctx);
}

const PlayPage = ({ className }, pageProps) => {
  const router = useRouter();
  const [ props, setProps ] = useState(pageProps);
  const [ currentRating, setCurrentRating ] = useState(props.game.displayRating || 0);
  const contentRef = useRef(null);
  const [ fullScreen, setFullScreen ] = useState(false);
    // Fetch new data on route change
	useEffect(() => {
		const handleRouteChange = async (url) => {
		  const gameSlug = url.split('/').pop(); // Assuming the slug is in the URL
		  const context = { params: { game: gameSlug } };
		  const result = await fetchProps(context);
	
		  if (result.props.game) {
			setProps(result.props);
		  }
		};
	
		router.events.on('routeChangeComplete', handleRouteChange);
	
		return () => {
		  router.events.off('routeChangeComplete', handleRouteChange);
		};
	  }, []);

  useEffect(() => {
    if (!props.preview) return;

    (async () => {
      const context = {
        params: {
          game: props.previewPath
        }
      };
      const result = await fetchProps(context);
      
      if (result.props.game) {
        setProps(result.props);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update content when props change
  useEffect(() => {}, [props.game]); // No-op or remove it

  if (router.isFallback || props.preview) {
    return (
      <FillScreenLayout>
        <Loader.Fill large />
      </FillScreenLayout>
    );
  }

  const randomGameGenerator = (supportsMobile) => {
	const relatedGames = props.relatedGames || [];
	return {
	  path: relatedGames.length > 0 ? relatedGames[props.randomIndex].path : '/',
	  thumbnailUrl: '/images/defaults/RandomGameButton.png',
	  supportsMobile
	};
  };

  let randomGame = randomGameGenerator(true);

  const analyticsOnPlayClick = () => {
	window.dataLayer.push({
		game: props.game.path.slice(1).replace(/-/g, "_"), // Remove leading slash & replace "-" with "_"
		event: "play_click"
	});
};

  const expandContent = () => {
    const el = contentRef.current;
    el.classList.remove('PlayPage__Description--collapsed');
  };

  const clickPlay = async (showGame) => {
    // TODO: show interstitial ad
    // gameAdEl.current.innerHTML = 'ADVERTISEMENT';
    // await wait(1500);
	analyticsOnPlayClick();
    showGame();
  };

  const gameGenreNames = props.game.categories.map((category, index) => {
    return category.name;
  });

  const description = getMetaDescription(props.game?.descriptionSimple);

  return (
    <GameLayout className={className}>
     <Meta
        title={props.game.title}
        description={description}
        openGraph={{
          title: props.game.title,
          description: description,
          image: props.game.thumbnailUrl,
          type: 'website'
        }}
      >
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'VideoGame',
              '@id': new URL(router.query.game, process.env.APP_CONFIG.URL).toString(),
              'name': props.game.title,
              'applicationCategory': 'IO-Game',
              'operatingSystem': 'Windows,Chrome OS,Linux,MacOS,Android,iOS',
              'image': props.game.thumbnailUrl,
              //'url': process.env.APP_CONFIG.URL + router.query.game,
              'url': new URL(router.query.game, process.env.APP_CONFIG.URL).toString(),
              'description': props.game.descriptionSimple.slice(0, 250),
              'inLanguage': [
                'English'
              ],
              'aggregateRating': {
                '@type': 'AggregateRating',
                'ratingValue': currentRating,
                'ratingCount': props.game.totalVotes
              },
              "offers":{"@type":"Offer","price":"0","priceCurrency":"USD","availability":"InStock"},
              'genre': gameGenreNames, 
              "gamePlatform":"Web Browser",
              "playMode":"SinglePlayer,MultiPlayer"
            }, null, 2)
          }}
        />

        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'FAQPage',
              'mainEntity': [
                { "@type": "Question",
                  "name":"What is "+props.game.title+"?",
                  "acceptedAnswer":
                    {"@type":"Answer",
                    "text":props.game.descriptionSimple.slice(0,250)}
                },
                { "@type": "Question",
                  "name":"How do I play "+props.game.title+"?",
                  "acceptedAnswer":
                    {"@type":"Answer",
                    "text":props?.game?.instructions ? props.game.instructions.slice(0,250) : ''}
                }
              ],
            }, null, 2)
          }}
        />


        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'BreadcrumbList',
              'itemListElement': [
                { "@type": "ListItem",
                  "position":1,
                  "name":"Home",
                  "item":process.env.APP_CONFIG.URL},
                { "@type": "ListItem",
                  "position":2,
                  "name":props.game.categories[0].name,
                 // "item":new URL('/' + props.game.categories[0].path , process.env.APP_CONFIG.URL).toString()},
                  "item": new URL(process.env.APP_CONFIG.URL + props.game.categories[0].path).toString()},
                { "@type": "ListItem",
                  "position":3,
                  "name":props.game.title}
              ],
            }, null, 2)
          }}
        />       


        {/*
        JK Edit - Remove these from the recommended games
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@type': 'ItemList',
              'itemListElement': props.relatedGames.map((game, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'url': game.path,
                'item': gameToLdJson(game)
              }))
            }, null, 2)
          }}
        /> */}

      </Meta>

      <Slot name='game'>
        <Flex fit className='--relative'>
          <GamePlayer
            key={props.game.id}
            preview
            previewImage={props.game.thumbnailUrl}
            previewTransition={demoFadeOut}
            fullScreen={fullScreen}
            supportsMobile={{ landscape: true, portrait: true }}
            gameSrc={props.game.embedUrl}
            onClickPlay={clickPlay}
            onClickExitFullScreen={() => setFullScreen(false)}
          >
            <Slot name='playButton'>
              <Flex alignCenter justifyCenter directionColumn>
                {
                  If(!props.game.supportsMobile, () => (
                    <div className='--padMd2 --textCenter PlayPage__noMobile'>
                      <Box>
                        <Block>
                          <span className='PlayPage__noMobileIcon'><Icon.FA icon={faDesktop} /> Desktop Only</span>
                          <p className='--marSm2__t'>
                            This game may not function correctly on your mobile device.
                          </p>
                        </Block>
                      </Box>
                    </div>
                  )).EndIf()
                }
                <Flex>
                  <Block>
                    <Button large secondary>Play&nbsp;<strong>{props.game.title}</strong></Button>
                  </Block>
                </Flex>
              </Flex>
            </Slot>
            {/* <Slot name='bottomPromo'>
              <Flex></Flex>
            </Slot> */}
            {/* <Slot name='interstitial'>
              <div ref={gameAdEl}></div>
            </Slot> */}
          </GamePlayer>
        </Flex>
      </Slot>

      <Block>
        <Flex gap1 container alignCenter fit>
          <GameRating game={props.game} entityId={props.entityId} />
          <Flex pullRight className='PlayPage__GamePlayer__Controls__Icon'>
            <Icon.FA icon={faExpand} onClick={() => setFullScreen(true)} />
          </Flex>
        </Flex>
      </Block>

      <Block>
        <Title>{props.game.title}</Title>
      </Block>

      <Block>
        <Flex container wrap directionRow className='PlayPage__Tags'>
          {
            For(props.game.categories, (category, i) => (
              <Flex key={i}>
                <GameTag alt={category.name} href={`${category.path}`}>
                  {category.name}
                </GameTag>
              </Flex>
            ))
          }
        </Flex>
      </Block>

      <Block>
        <div ref={contentRef} className='Content PlayPage__Description PlayPage__Description--collapsed'>
		<div dangerouslySetInnerHTML={{ __html: props.game.description }} />

		  {props.game.instructions && (
			<div className='PlayPage__Description__Instructions'>
				<h3>How do you play {props.game.title}?</h3>
				<div dangerouslySetInnerHTML={{ __html: props.game.instructions }} />
			</div>
			)}

			{props.game.developer && (
			<div className='PlayPage__Description__Developer'>
				<h3>Who made {props.game.title}?</h3>
				<div dangerouslySetInnerHTML={{ __html: props.game.developer }} />
			</div>
			)}
			
			{props.game.wikipage&& (
			<div className='PlayPage__Description__Wikipage'>
				<h3>More about {props.game.title}</h3>
				<div dangerouslySetInnerHTML={{ __html: props.game.wikipage }} />
			</div>
			)}

        </div>
        <div className='PlayPage__Description__ReadMore' onClick={expandContent}><span>Read More</span></div>
      </Block>

      {
        If(props.game.youtubeLinks.length > 0, () => (
          <Block>
            <Content className='PlayPage__Description'>
              <h3>Gameplay Video{props.game.youtubeLinks.length > 0 ? 's' : ''}</h3>
              {
                For(props.game.youtubeLinks, (youtubeLink) => (
                  <Block>
                    <iframe 
                      title={youtubeLink.title}
                      frameborder='0' 
                      scrolling='no' 
                      marginheight='0' 
                      marginwidth='0'
                      width='364' 
                      height='205' 
                      type='text/html' 
                      src={`//www.youtube.com/embed/${youtubeLink.video_id}?autoplay=0&fs=1&iv_load_policy=3&showinfo=0&rel=0&cc_load_policy=0&start=0&end=0`}
                    />
                  </Block>
                ))
              }  
            </Content>
          </Block>
        ))
          .EndIf()
      }

      <Block>
        <CallToActionTile game={randomGame}>
          Want to try something different?
        </CallToActionTile>
        <div className='PlayPage__GameTiles'>
          {
            For(props.relatedGames, (game, index) => (
              <IoGameTile
                key={game.id + index}
                game={game}
              />
            ))
          }
        </div>
      </Block>
    </GameLayout>
  );
};

export default createPage('PlayPage', { styles }, PlayPage);
